import { useEffect, useMemo, useState } from "react";
import { Play, CheckCircle2, History, AlertTriangle } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/hooks/useToast";
import {
  runAccruals,
  getAccrualHistory,
  type AccrualRunResponse,
  type AccrualRunEntry,
  type AccrualHistoryRow,
} from "@/api/leavePolicy";
import { listEmployees } from "@/api/employees";
import type { EmployeeListItem } from "@/types/employee";
import { formatDate } from "@/lib/utils";

const LEAVE_TYPES = [
  { value: "", label: "All leave types" },
  { value: "PTO", label: "PTO" },
  { value: "SICK", label: "SICK" },
  { value: "PERSONAL", label: "PERSONAL" },
  { value: "BEREAVEMENT", label: "BEREAVEMENT" },
  { value: "FMLA", label: "FMLA" },
  { value: "UNPAID", label: "UNPAID" },
  { value: "JURY_DUTY", label: "JURY_DUTY" },
  { value: "VOLUNTEER", label: "VOLUNTEER" },
  { value: "CAREGIVING", label: "CAREGIVING" },
  { value: "MENTAL_HEALTH", label: "MENTAL_HEALTH" },
];

function currentPeriodKey(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function isValidPeriodKey(v: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
}

export function AccrualRunnerPanel() {
  const { addToast } = useToast();
  const [periodKey, setPeriodKey] = useState(currentPeriodKey());
  const [leaveType, setLeaveType] = useState("");
  const [busy, setBusy] = useState<"dry" | "apply" | null>(null);
  const [preview, setPreview] = useState<AccrualRunResponse | null>(null);
  const [history, setHistory] = useState<AccrualHistoryRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [employeeIndex, setEmployeeIndex] = useState<
    Record<string, EmployeeListItem>
  >({});

  useEffect(() => {
    // Build a small id→name lookup so preview + history tables read nicely.
    (async () => {
      try {
        const res = await listEmployees(1, 100);
        const idx: Record<string, EmployeeListItem> = {};
        for (const e of res.items) idx[e.id] = e;
        setEmployeeIndex(idx);
      } catch {
        // Non-fatal — we'll fall back to raw UUIDs.
      }
    })();
    void loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const rows = await getAccrualHistory({ limit: 50 });
      setHistory(rows);
    } catch {
      // Don't blast a toast on initial load — HR may not have run yet.
    } finally {
      setLoadingHistory(false);
    }
  };

  const employeeName = (id: string) => {
    const e = employeeIndex[id];
    return e ? `${e.first_name} ${e.last_name}` : id.slice(0, 8) + "…";
  };

  const invalidPeriod = !isValidPeriodKey(periodKey);

  const run = async (dryRun: boolean) => {
    if (invalidPeriod) {
      addToast("error", "Period must look like YYYY-MM");
      return;
    }
    setBusy(dryRun ? "dry" : "apply");
    try {
      const res = await runAccruals({
        period_key: periodKey,
        dry_run: dryRun,
        leave_type: leaveType || undefined,
      });
      setPreview(res);
      if (dryRun) {
        addToast(
          "info",
          `Dry run — ${res.accrued_rows} employee(s) would accrue ${res.total_days} day(s)`
        );
      } else {
        addToast(
          "success",
          `Applied — ${res.accrued_rows} employee(s) accrued ${res.total_days} day(s)`
        );
        await loadHistory();
      }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { detail?: string } } };
      const detail = ax.response?.data?.detail || "Accrual run failed";
      addToast("error", detail);
    } finally {
      setBusy(null);
    }
  };

  const accruedRows: AccrualRunEntry[] = useMemo(
    () =>
      preview
        ? preview.entries.filter((e) => !e.skipped && e.days_accrued > 0)
        : [],
    [preview]
  );
  const skippedRows: AccrualRunEntry[] = useMemo(
    () => (preview ? preview.entries.filter((e) => e.skipped) : []),
    [preview]
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Run accruals</CardTitle>
        </CardHeader>
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            Apply monthly accruals for every active employee × monthly policy.
            Runs are idempotent per period — re-running the same period will
            skip employees that already have a ledger entry.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              label="Period (YYYY-MM)"
              value={periodKey}
              onChange={(e) => setPeriodKey(e.target.value)}
              placeholder={currentPeriodKey()}
            />
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Leave type filter
              </label>
              <Select
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
                options={LEAVE_TYPES}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                variant="secondary"
                onClick={() => run(true)}
                isLoading={busy === "dry"}
                disabled={busy !== null || invalidPeriod}
              >
                <Play className="h-4 w-4" />
                Dry run
              </Button>
              <Button
                onClick={() => run(false)}
                isLoading={busy === "apply"}
                disabled={busy !== null || invalidPeriod || !preview?.dry_run}
                title={
                  !preview?.dry_run
                    ? "Run a dry run first to preview"
                    : undefined
                }
              >
                <CheckCircle2 className="h-4 w-4" />
                Apply
              </Button>
            </div>
          </div>
          {invalidPeriod && (
            <p className="text-xs text-warning flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              Period must be formatted as YYYY-MM
            </p>
          )}
        </div>
      </Card>

      {preview && (
        <Card>
          <CardHeader>
            <CardTitle>
              {preview.dry_run ? "Preview" : "Applied"} · {preview.period_key}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant={preview.dry_run ? "info" : "success"}>
                {preview.dry_run ? "Dry run" : "Applied"}
              </Badge>
              <span className="text-xs text-text-muted">
                {preview.accrued_rows} accrued · {preview.total_days} day
                {preview.total_days === 1 ? "" : "s"} · {skippedRows.length}{" "}
                skipped
              </span>
            </div>
          </CardHeader>

          {accruedRows.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-6">
              No accruals to apply for this period.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-text-muted uppercase tracking-wider border-b border-border/40">
                    <th className="text-left py-2 pr-3">Employee</th>
                    <th className="text-left py-2 pr-3">Leave type</th>
                    <th className="text-right py-2 pr-3">Before</th>
                    <th className="text-right py-2 pr-3">Accrued</th>
                    <th className="text-right py-2 pr-3">After</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/30">
                  {accruedRows.map((r, i) => (
                    <tr key={`${r.employee_id}-${r.policy_id}-${i}`}>
                      <td className="py-2 pr-3 text-text-primary">
                        {employeeName(r.employee_id)}
                      </td>
                      <td className="py-2 pr-3 text-text-muted">
                        {r.leave_type}
                      </td>
                      <td className="py-2 pr-3 text-right text-text-muted">
                        {r.balance_before.toFixed(2)}
                      </td>
                      <td className="py-2 pr-3 text-right font-medium text-accent">
                        +{r.days_accrued.toFixed(2)}
                      </td>
                      <td className="py-2 pr-3 text-right text-text-primary">
                        {r.balance_after.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {skippedRows.length > 0 && (
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer text-text-muted hover:text-text-primary">
                Skipped ({skippedRows.length})
              </summary>
              <div className="mt-2 space-y-1">
                {skippedRows.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-xs text-text-muted"
                  >
                    <span>
                      {employeeName(r.employee_id)} · {r.leave_type}
                    </span>
                    <span>{r.skip_reason}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <History className="h-4 w-4" />
              Accrual history
            </span>
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={loadHistory}>
            Refresh
          </Button>
        </CardHeader>
        {loadingHistory ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-6">
            No accrual runs recorded yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-text-muted uppercase tracking-wider border-b border-border/40">
                  <th className="text-left py-2 pr-3">Period</th>
                  <th className="text-left py-2 pr-3">Employee</th>
                  <th className="text-left py-2 pr-3">Leave type</th>
                  <th className="text-right py-2 pr-3">Accrued</th>
                  <th className="text-right py-2 pr-3">Balance after</th>
                  <th className="text-left py-2 pr-3">Run at</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {history.map((r) => (
                  <tr key={r.id}>
                    <td className="py-2 pr-3 text-text-muted">
                      {r.period_key}
                    </td>
                    <td className="py-2 pr-3 text-text-primary">
                      {employeeName(r.employee_id)}
                    </td>
                    <td className="py-2 pr-3 text-text-muted">
                      {r.leave_type}
                    </td>
                    <td className="py-2 pr-3 text-right font-medium">
                      +{r.days_accrued.toFixed(2)}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {r.balance_after.toFixed(2)}
                    </td>
                    <td className="py-2 pr-3 text-xs text-text-muted">
                      {r.created_at ? formatDate(r.created_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
